import * as B from '@blueprintjs/core';
import {useMutation} from '@tanstack/react-query';
import moment from 'moment';
import React from 'react';

import {CopyableText} from 'app/components/CopyableText';
import {MultifeatureAnalysisConfig} from 'app/components/Dashboard/types';
import Modal from 'app/components/Modal/Modal';
import {usePushNotification} from 'app/components/Notification';
import psmStyles from 'app/components/PublicMap/styles.module.scss';
import {api} from 'app/modules/Remote';
import {getAreaUnit} from 'app/modules/Remote/Organization';
import {useUserInfo} from 'app/utils/featureFlags';
import {ImageLayerInfo} from 'app/utils/layers';
import {getLayer} from 'app/utils/layerUtils';
import {makeAppUrl} from 'app/utils/routeUtils';

type ShareWidgetResponse = {
  widgetId: string;
  configId: string;
};

export const useLensWidgetShareDialog = (widgetConfig: MultifeatureAnalysisConfig) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [shareLink, setShareLink] = React.useState<string | null>(null);
  const [titleInput, setTitleInput] = React.useState<string>(widgetConfig.title);
  const [descriptionInput, setDescriptionInput] = React.useState<string>(
    widgetConfig.description || ''
  );
  const pushNotification = usePushNotification();

  const [organization] = useUserInfo();
  const organizationId = organization.get('id');
  const areaUnit = getAreaUnit(organization);

  // Per-share category subset, categorical (area_counts) widgets only — matches widget_data_type on
  // the backend, which validates external display options as categorical only for area_counts. The
  // shared chart shows exactly the selected categories, overriding any widget-level subset.
  const isCategorical = widgetConfig.statAggregation === 'area_counts';
  const categoryEntries = React.useMemo<{id: string; label: string; color?: string}[]>(() => {
    const layer = getLayer(widgetConfig.layerKey);
    if (!isCategorical || !layer || layer.type !== 'image') return [];
    const legendMap = (layer as ImageLayerInfo).layerLegendMap;
    if (!legendMap) return [];
    return Object.entries(legendMap).map(([id, info]) => ({id, label: info.label || id, color: info.color}));
  }, [isCategorical, widgetConfig.layerKey]);

  const [selectedCategories, setSelectedCategories] = React.useState<string[]>([]);

  const shareMutation = useMutation<ShareWidgetResponse, Error>({
    mutationKey: ['share-lens-widget', widgetConfig.id, organizationId],
    mutationFn: async () => {
      const includedCategories =
        isCategorical && selectedCategories.length > 0 ? selectedCategories : undefined;
      const response = await api.dashboardStats(organizationId).shareWidget(widgetConfig.id, {
        title: titleInput,
        description: descriptionInput,
        areaUnit,
        ...(includedCategories ? {includedCategories} : {}),
      });
      const configId = response.getIn(['data', 'configId']) as string | undefined;
      const widgetId = response.getIn(['data', 'widgetId']) as string | undefined;

      if (!configId || !widgetId) {
        throw new Error('Unexpected response while sharing widget.');
      }

      return {configId, widgetId};
    },
    onSuccess: ({configId}) => {
      const generatedLink = makeAppUrl(`/p/w/${configId}`);
      setShareLink(generatedLink);
      pushNotification({
        message: 'Widget shared. Copy the link below to share it externally.',
        options: {intent: B.Intent.SUCCESS},
      });
    },
    onError: (error) => {
      console.error('Error sharing chart', error);
      pushNotification({
        message: error?.message ?? 'Error sharing chart.',
        options: {intent: B.Intent.DANGER},
      });
    },
  });

  const openModal = React.useCallback(() => {
    setShareLink(null);
    shareMutation.reset();
    setTitleInput(widgetConfig.displayOptions?.title || widgetConfig.title || '');
    setDescriptionInput(widgetConfig.displayOptions?.description || widgetConfig.description || '');
    setSelectedCategories(
      widgetConfig.displayOptions?.includedCategories ?? categoryEntries.map((c) => c.id)
    );
    setIsOpen(true);
  }, [shareMutation, widgetConfig, categoryEntries]);

  const closeModal = React.useCallback(
    (event?: React.SyntheticEvent<HTMLElement>) => {
      event?.preventDefault?.();
      if (!shareMutation.isPending) {
        setIsOpen(false);
        shareMutation.reset();
        setShareLink(null);
      }
    },
    [shareMutation]
  );

  const confirmShare = React.useCallback(() => {
    if (!shareMutation.isPending) {
      shareMutation.mutate();
    }
  }, [shareMutation]);

  const featureSelectionLabel = React.useMemo(() => {
    if (!widgetConfig.featureIds || widgetConfig.featureIds.length === 0) {
      return 'All properties';
    }
    return `${widgetConfig.featureIds.length} properties included`;
  }, [widgetConfig.featureIds]);

  const dialog = (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      headerText="Share Chart"
      canOutsideClickClose={!shareMutation.isPending}
      hideActions={true}
    >
      <div className={psmStyles.shareModal}>
        <div>
          <div className={psmStyles.shareDescriptionModal} style={{marginTop: '2rem'}}>
            Share an interactive dashboard chart with partners—no Lens account needed.{' '}
            <a
              href="https://lens.tech/support/article/151-lens-share-link#share"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more here.
            </a>
          </div>
          <table className={psmStyles.shareModalDetails}>
            <tbody>
              <tr>
                <th colSpan={2}>
                  <div className={psmStyles.sectionHeader}>
                    <div>Chart details</div>
                    <div className={psmStyles.sectionHeaderDivider} />
                  </div>
                </th>
              </tr>
              {shareMutation.isError && (
                <tr>
                  <td colSpan={2}>
                    <B.Callout intent={B.Intent.DANGER}>
                      {shareMutation.error?.message || 'Error sharing chart.'}
                    </B.Callout>
                  </td>
                </tr>
              )}
              <tr>
                <th>Title</th>
                <td>
                  {shareLink ? (
                    widgetConfig.displayOptions?.title || titleInput
                  ) : (
                    <B.InputGroup
                      placeholder="Enter a title"
                      value={titleInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTitleInput(e.target.value)
                      }
                    />
                  )}
                </td>
              </tr>
              <tr>
                <th>Description</th>
                <td>
                  {shareLink ? (
                    widgetConfig.displayOptions?.description || descriptionInput
                  ) : (
                    <B.TextArea
                      fill
                      placeholder="Add an optional description"
                      value={descriptionInput}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setDescriptionInput(e.target.value)
                      }
                    />
                  )}
                </td>
              </tr>

              {!shareLink && isCategorical && categoryEntries.length > 0 && (
                <tr>
                  <th>Categories</th>
                  <td>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
                      {categoryEntries.map(({id, label, color}) => (
                        <B.Checkbox
                          key={id}
                          checked={selectedCategories.includes(id)}
                          onChange={() =>
                            setSelectedCategories((prev) =>
                              prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
                            )
                          }
                          labelElement={
                            <span>
                              {color && (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    borderRadius: 2,
                                    backgroundColor: color,
                                    marginRight: 6,
                                    verticalAlign: 'middle',
                                  }}
                                />
                              )}
                              {label}
                            </span>
                          }
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              )}
              <tr>
                <th>Time range</th>
                <td>
                  {widgetConfig.startDate
                    ? moment(widgetConfig.startDate).utc().format('DD MMM, YYYY')
                    : 'Earliest available date'}{' '}
                  -{' '}
                  {widgetConfig.endDate
                    ? moment(widgetConfig.endDate).utc().format('DD MMM, YYYY')
                    : 'Present'}
                </td>
              </tr>
              <tr>
                <th>Features</th>
                <td>{featureSelectionLabel}</td>
              </tr>
              {!shareLink && (
                <tr>
                  <td colSpan={2}>
                    <B.Button
                      className={psmStyles.shareLinkConfirmButton}
                      intent={B.Intent.PRIMARY}
                      onClick={confirmShare}
                      loading={shareMutation.isPending}
                    >
                      Generate share link
                    </B.Button>
                  </td>
                </tr>
              )}
              {shareLink && (
                <>
                  <tr>
                    <th colSpan={2}>
                      <div className={psmStyles.sectionHeader}>
                        <div>Share</div>
                        <div className={psmStyles.sectionHeaderDivider} />
                      </div>
                    </th>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <CopyableText
                        description={
                          <span>
                            <div className={psmStyles.shareSectionHead}>
                              <b>Lens Chart Link</b>
                            </div>
                            <span className={psmStyles.shareLinkShareDetails}>
                              Share this dashboard chart externally
                            </span>
                          </span>
                        }
                        copyableText={shareLink}
                      />
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );

  return {
    openModal,
    dialog,
  };
};
